import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * EntityApiService (Angular 18) — Personnel / Equipment / Facility entities
 * and their schedules.
 *
 * Replaces apps/b2b-ui/common/services/api/entityApi/entityApi.service.js.
 *
 * Endpoints follow a `<type>` path discriminator pattern. URL shape:
 *   `/v2/api/<type>/<id>` for detail/update/remove
 *   `/v1/api/<type>` for create
 *   `/v1/api/<type>/convert/<id>` for convert
 *   `/v1/api/entity/schedules/{event,resource,owner,rsvp}/...` for schedules
 *
 * Legacy quirk preserved: `create()` strips the `type` field from the body
 * (it lives in the URL path, not the payload). The original code does
 * `data.type = null; delete data.type;` — same effect.
 */
const API_BASE_V1 = '/v1/api';
const API_BASE_V2 = '/v2/api';
const ENTITY_SCHEDULES_BASE = '/v1/api/entity/schedules';

const ENTITY_LIST_FIELDS = [...PAGINATION_SEARCH_FIELDS, 'resourceCategoryIds'] as const;

interface EntityRef {
  type: string;
  id: number | string;
}

@Injectable({ providedIn: 'root' })
export class EntityApiService {
  private readonly http = inject(HttpClient);

  detail(props: EntityRef): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${API_BASE_V2}/${props.type}/${props.id}`));
  }

  update(props: EntityRef & Record<string, unknown>): Promise<AjsResponse<unknown>> {
    let data: Record<string, unknown> = {
      id: props.id,
      name: props['name'],
      description: props['description'],
    };

    if (props.type === 'personnel') {
      data = {
        id: props.id,
        tenantId: props['tenantId'],
        active: props['active'],
        status: props['status'],
        confirmed: props['confirmed'],
        onBoarded: props['onBoarded'],
        firstName: props['firstName'],
        lastName: props['lastName'],
        phoneNumber: props['phoneNumber'],
        title: props['title'],
        departmentId: props['departmentId'],
        streetAddress1: props['streetAddress1'],
        streetAddress2: props['streetAddress2'],
        country: props['country'],
        state: props['state'],
        city: props['city'],
        zipCode: props['zipCode'],
        email: props['email'],
        roles: props['roles'],
      };
    } else if (props.type === 'facilities') {
      data['capacity'] = props['capacity'];
    }

    if (props.type === 'equipment' || props.type === 'facilities') {
      if (props['contactId']) data['contactId'] = props['contactId'];
      if (props['contactName']) data['contactName'] = props['contactName'];
      if (props['contactEmail']) data['contactEmail'] = props['contactEmail'];
      if (props['contactPhoneNumber']) data['contactPhoneNumber'] = props['contactPhoneNumber'];
    }

    if (props['profileImage']) data['profileImage'] = props['profileImage'];
    if (props['image']) data['image'] = props['image'];

    return wrapAjs(this.http.put<unknown>(`${API_BASE_V2}/${props.type}/${props.id}`, data));
  }

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    // Legacy used `paths: ['type']` to append `/type` to the URL before the
    // querystring. We mirror that by reading `props.type` and slotting it
    // into the URL path; the field is stripped from the querystring whitelist.
    const type = props?.['type'] as string | undefined;
    const url = type ? `${API_BASE_V2}/${type}` : API_BASE_V2;
    return wrapAjs(
      this.http.get<unknown>(url, {
        params: buildParams(props, ENTITY_LIST_FIELDS),
      }),
    );
  }

  create(props: EntityRef & Record<string, unknown>): Promise<AjsResponse<unknown>> {
    const { type, ...data } = props;
    return wrapAjs(this.http.post<unknown>(`${API_BASE_V1}/${type}`, data));
  }

  remove(props: EntityRef): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${API_BASE_V2}/${props.type}/${props.id}`));
  }

  convert(props: EntityRef): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(`${API_BASE_V1}/${props.type}/convert/${props.id}`, null));
  }

  readonly schedules = {
    event: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${ENTITY_SCHEDULES_BASE}/event/${id}`)),

    updateEvent: (props: { id: number | string; [key: string]: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(`${ENTITY_SCHEDULES_BASE}/event/${props.id}`, props)),

    rsvp: (props: { id: number | string; rsvpState: string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(`${ENTITY_SCHEDULES_BASE}/rsvp`, {
          id: props.id,
          rsvpState: props.rsvpState,
        }),
      ),

    resource: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${ENTITY_SCHEDULES_BASE}/resource/${id}`)),

    owner: (props: {
      ownerId: number | string;
      startTime?: unknown;
      endTime?: unknown;
    }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${ENTITY_SCHEDULES_BASE}/owner/${props.ownerId}`, {
          params: buildParams({ startTime: props.startTime, endTime: props.endTime }, ['startTime', 'endTime']),
        }),
      ),
  };
}
